import React, { useEffect, useState } from "react";
import {
    Button,
    Card,
    Dropdown,
    Input,
    Modal,
    Select,
    Space,
    Table,
    Tag,
    Menu,
    Divider,
    Typography,
    InputNumber, Popover
} from "antd";
import { t } from "i18next";
import { useHistory } from "react-router-dom";
import SimpleLayout from "../../core/components/layout/SimpleLayout";
import Helper from "../../core/Helper";
import ApiService from "../../core/service/ApiService";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";

export default function OperationList() {
    const history = useHistory();
    const [operations, setOperations] = useState([]);
    const [filter, setFilter] = useState({
        text: null,
        unitId: -1
    })
    const [institutions, setInstitutions] = useState([]);
    const [selectedInstitution, setSelectedInstitution] = useState('C');
    const [filterText, setFilterText] = useState('')
    const [prices, setPrices] = useState([])
    const [units, setUnits] = useState([])
    const { firmSettings, user, hasPermission } = useUser();

    const getOperations = async () => {
        const typeRes = await ApiService.Appointment.GetAppointmentTypes();
        await Promise.all([
            ApiService.Institution.GetInstitutionList(true).then(res => {
                if (res.success) {
                    const data = [
                        { name: t('Current Operation Prices'), id: 'C' },
                        ...(typeRes.data.map((item) => {
                            item.id = `RT${item.id}`
                            return item;
                        })),
                        ...(res.data.map(x => {
                            x.id = `K${x.id}`
                            return x;
                        }))
                    ]
                    setInstitutions(data)
                }
            }),
            ApiService.Operation.GetAllOperationList().then(res => {
                if (res.success) {
                    setOperations(res.data)
                }
            }),
            ApiService.Unit.GetUnitList().then(res => {
                if (res.success) {
                    setUnits([
                        { id: -1, name: t('All') },
                        ...res.data.map(x => ({ ...x, name: `${x?.firm?.name} - ${x.name}` }))
                    ])
                }
            }),
        ])
    }

    const deleteOperation = async (operationId) => {
        const res = await ApiService.Operation.DeleteOperation(operationId);
        if (res.success) {
            await getOperations();
        }
    }

    const downloadExcel = async () => {
        const res = await ApiService.Operation.GetExcelPriceList(selectedInstitution)
        if (res.success) {
            Helper.File.saveFile({
                blobLink: res.data,
                fileName: res.fileName
            })
        }
    }

    const uploadExcel = async (file) => {
        const res = await ApiService.Operation.UpdatePriceFromExcel({
            excelBase64: file,
            institutionId: selectedInstitution
        })

        if (res.success) {
            await getOperations()
        }
    }

    const getInstitutionPriceList = async () => {
        const res = await ApiService.Operation.GetInstitutionPriceList(selectedInstitution.replaceAll('K', ''), operations.map(x => x.id));
        if (res.success) {
            setPrices(res.data)
        }
    }

    const getAppointmentTypePriceList = async () => {
        const res = await ApiService.Operation.GetAppointmentTypePriceList(selectedInstitution.replaceAll('RT', ''), operations.map(x => x.id));
        if (res.success) {
            setPrices(res.data)
        }
    }

    const renderAction = (row) => {
        if (!hasPermission(PERMISSIONS.FIRM_OPERATION_MANAGER)) {
            return ``
        }

        return (
            <Space>
                <a key="edit" onClick={() => history.push(`/app/system/operations/edit/${row.id}`)}>{t('edit')}</a>
                <a key="delete" onClick={async () => { await deleteOperation(row.id) }} style={{ color: 'red' }}>{t('Delete')}</a>
            </Space>
        )
    }

    useEffect(() => {
        if (selectedInstitution.startsWith('K')) {
            getInstitutionPriceList()
        }
        else if (selectedInstitution.startsWith('RT')) {
            getAppointmentTypePriceList()
        }
        else {
            setPrices([])
        }
    }, [selectedInstitution]);

    const filterData = operations.filter(x => (filter.unitId === -1 || x?.unit?.id === filter.unitId))
        .filter(x => !filter.text || (
            Helper.Text.normalizeText(x.name).includes(Helper.Text.normalizeText(filter.text)) ||
            Helper.Text.normalizeText(x.sutCodePart1 || '').includes(Helper.Text.normalizeText(filter.text)) ||
            Helper.Text.normalizeText(x.loinc || '').includes(Helper.Text.normalizeText(filter.text)) ||
            Helper.Text.normalizeText(user.tags.filter(t => x.tagList.includes(t.id)).map(t => t.name).join(' ')).includes(Helper.Text.normalizeText(filter.text))
        )
        )

    const renderText = (text, width) => {
        return (
            <Popover content={text}>
                <Typography.Text style={{ width: width }} ellipsis>{text}</Typography.Text>
            </Popover>
        )
    }

    const getColumns = () => {
        let columns = [
            { key: 'unit', dataIndex: 'unit', title: t('Unit'), width: 100, render: (text, row) => row.unit.name },
            { key: 'firmName', dataIndex: 'firmName', title: t('Firm'), render: (text, row) => row?.unit?.firm?.name, width: 100 },
            { key: 'name', dataIndex: 'name', title: t('Name'), width: 180 },
            {
                key: 'price',
                dataIndex: 'price',
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {t('Price')}
                        <Typography.Text style={{ width: 120 }} ellipsis={true}>
                            <b>({institutions.find(x => x.id === selectedInstitution)?.name})</b>
                        </Typography.Text>
                    </div>
                ),
                render: (text, row) => {
                    if (hasPermission(PERMISSIONS.FIRM_OPERATION_MANAGER)) {
                        // const inst = institutions.find(x => x.id === selectedInstitution)
                        return (
                            <InputNumber
                                size={'small'}
                                min={0}
                                value={prices?.length > 0 ? prices.find(x => x.operationId === row.id)?.price : text}
                                onPressEnter={async (e) => {
                                    const newPrice = e.target.value;
                                    const res = await ApiService.Operation.UpdateOperationPrice(row.id, selectedInstitution, newPrice);
                                    if (res.success) {
                                        setPrices(prices.map(x => {
                                            if (x.operationId === row.id) {
                                                return {
                                                    ...x,
                                                    price: newPrice
                                                }
                                            }
                                            return x
                                        }))
                                    }
                                }}
                            />
                        )
                    }

                    if (prices.length === 0)
                        return Helper.Currency.format(text)

                    const price = prices.find(x => x.operationId === row.id)
                    if (price)
                        return Helper.Currency.format(price.price)

                },
                width: 225
            },
            {
                key: 'tagList',
                dataIndex: 'tagList',
                title: t('Tags'),
                render: (tags, row) => {
                    return user?.tags?.filter(x => tags.includes(x.id))?.map(t => <Tag key={t.id} color={t.color}>{t.name}</Tag>)
                }
            },
            { key: 'sutCodePart1', dataIndex: 'sutCodePart1', width: 150, title: t('SUT CODE-1'), render: (text, row) => row.sutCodePart1 || '-' },
            { key: 'loinc', dataIndex: 'loinc', width: 150, title: t('Loinc'), render: (text, row) => row.loinc || '-' },
            { key: 'huv', dataIndex: 'huv', width: 150, title: t('HUV'), render: (text, row) => renderText(row.huv, 140) },
            { key: 'ttb', dataIndex: 'ttb', width: 150, title: t('TTB'), render: (text, row) => renderText(row.ttb, 140) },
            { key: '#', dataIndex: '#', title: '#', render: (text, row) => renderAction(row) },
        ]

        if (!hasPermission(PERMISSIONS.FIRM_OPERATION_MANAGER)) {
            columns = columns.filter(x => !['sutCodePart1', 'sutCodePart2'].includes(x.key))
        }

        return columns;
    }

    useEffect(() => {
        getOperations()
    }, []);

    return (
        <SimpleLayout fullWidth={true}>
            <Card>
                <Space style={{ marginBottom: 12 }}>
                    <Button size={'small'} onClick={() => { history.push(`/app/system/operations/create`) }} type={'primary'}>{t('New')}</Button>
                    <Input
                        size={'small'}
                        style={{ width: 300 }}
                        value={filter.text}
                        placeholder={`${t('Search')}: ${t('Name')}, ${t('Unit')}, ${t('SUT')} ${t('Tags')}`}
                        onChange={(e) => {
                            setFilter({
                                ...filter,
                                text: e.target.value
                            })
                        }}
                    />
                    <Select
                        style={{ minWidth: 150 }}
                        showSearch={true}
                        placeholder={t('Unit')}
                        value={filter.unitId}
                        size={'small'}
                        filterOption={(input, option) => {
                            return Helper.Text.normalizeText(JSON.stringify(option)).includes(Helper.Text.normalizeText(input))
                        }}
                        onChange={(val) => {
                            setFilter({
                                ...filter,
                                unitId: val
                            })
                        }}
                        fieldNames={{
                            label: 'name',
                            value: 'id'
                        }}
                        options={units}
                    />
                    <Select
                        size={'small'}
                        value={selectedInstitution}
                        showSearch={true}
                        style={{ minWidth: 150 }}
                        filterOption={(input, option) => {
                            setFilterText(input)
                            return true
                        }}
                        onInputKeyDown={(e) => {
                            if (e.code === "Escape") {
                                setFilterText('')
                            }
                        }}
                        onBlur={() => {
                            setFilterText('')
                        }}
                        onChange={(val) => { setSelectedInstitution(val) }}
                    >
                        <Select.OptGroup label={'İşlem'}>
                            {
                                institutions
                                    .filter(x => Helper.Text.normalizeText(x.name).includes(Helper.Text.normalizeText(filterText)))
                                    .filter(x => x.id.startsWith('C')).map(x => (
                                        <Select.Option key={x.id} value={x.id}>
                                            {x.name}
                                        </Select.Option>
                                    ))
                            }
                        </Select.OptGroup>
                        <Select.OptGroup label={'Randevu Türü'}>
                            {
                                institutions
                                    .filter(x => Helper.Text.normalizeText(x.name).includes(Helper.Text.normalizeText(filterText)))
                                    .filter(x => x.id.startsWith('RT')).map(x => (
                                        <Select.Option key={x.id} value={x.id}>
                                            {x.name}
                                        </Select.Option>
                                    ))
                            }
                        </Select.OptGroup>
                        <Select.OptGroup label={'Kurum'}>
                            {
                                institutions
                                    .filter(x => Helper.Text.normalizeText(x.name).includes(Helper.Text.normalizeText(filterText)))
                                    .filter(x => x.id.startsWith('K')).map(x => (
                                        <Select.Option key={x.id} value={x.id}>
                                            {x.name}
                                        </Select.Option>
                                    ))
                            }
                        </Select.OptGroup>
                    </Select>
                    {
                        (hasPermission(PERMISSIONS.FIRM_INSTITUTION_MANAGER) || user.institutionFirms.map(x => x.institutionId).includes(selectedInstitution)) && (
                            <div>
                                <Dropdown
                                    overlay={
                                        <Menu>
                                            <Menu.Item key="1" onClick={downloadExcel}>
                                                {t('Export Excel') + ` (${institutions.find(x => x.id === selectedInstitution)?.name})`}
                                            </Menu.Item>
                                            <Menu.Item key="3" onClick={async () => {
                                                const resFile = await Helper.File.getFileAsBase64({ accept: '*.xlsx' });
                                                if (resFile.data) {
                                                    Modal.confirm({
                                                        closable: true,
                                                        onOk: async () => {
                                                            await uploadExcel(resFile.data, 1)
                                                        },
                                                        onCancel: async (args) => { },
                                                        okText: t('Yes'),
                                                        cancelText: t('No'),
                                                        title: t('Update Operation Prices'),
                                                        content: t('Do you want to continue to update operation prices?')
                                                    })
                                                }
                                            }}>
                                                {t('Import Excel') + ` (${institutions.find(x => x.id === selectedInstitution)?.name})`}
                                            </Menu.Item>
                                        </Menu>
                                    }
                                >
                                    <Button size={'small'} type={'primary'}>{t('Excel Islemleri')}</Button>
                                </Dropdown>
                            </div>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.FIRM_OPERATION_MANAGER) && (
                            <Button
                                size={'small'}
                                type={'primary'}
                                onClick={() => history.push('/app/system/operations/price-update')}
                            >
                                {t('Fiyat Güncelleme')}
                            </Button>
                        )
                    }
                </Space>
                <Table
                    size={'small'}
                    dataSource={filterData}
                    rowKey={'id'}
                    pagination={{ pageSize: 25, size: 'small' }}
                    scroll={{ y: window.innerHeight - 200 }}
                    columns={getColumns()}
                />
            </Card>
        </SimpleLayout>
    )
}
